import './vehicleConfirmationPage.scss';

import { ThemeProvider } from '@mui/material/styles';
import isEmpty from 'lodash/isEmpty';
import rangeRight from 'lodash/rangeRight';
import toString from 'lodash/toString';
import { ReactNode, useEffect } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { Alert } from '../../components/Alert/Alert';
import { Button } from '../../components/Button/Button';
import { CircularProgressFullScreen } from '../../components/CircularProgressFullScreen/CircularProgressFullScreen';
import { Header } from '../../components/Header/Header';
import { NavigationPane } from '../../components/NavigationPane/NavigationPane';
import { Select } from '../../components/Select/Select';
import { resetStore } from '../../store/_core/resetStore';
import useApiStore from '../../store/apiStore/useApiStore';
import useDashboardStore from '../../store/dashboardStore/useDashboardStore';
import useParameterStore from '../../store/parameterStore/useParameterStore';
import { useNavigateWithCallback } from '../../store/routeStore/hooks/useNavigateWithCallback';
import useThemeStore from '../../store/themeStore/useThemeStore';
import { UpdateVehicleParams } from '../../store/vehicleConfirmationStore/types/UpdateVehicleParams';
import useVehicleConfirmationStore from '../../store/vehicleConfirmationStore/useVehicleConfirmationStore';
import { locator } from '../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const navigateWithCallback = useNavigateWithCallback();
  const { vehicleId: paramVehicleId } = useParams();

  const { createTheme } = useThemeStore();
  const theme = createTheme();

  const { isLoading } = useApiStore();
  const { t } = useTranslation();

  const { hasDuplicateApplication } = useParameterStore();

  const {
    computed: { isVehicleEmpty, vehicleId },
    fetchVehicleByApplicationAutopayNumberFailedMessage,
    fetchVehicleByIdFailedMessage,
    isFetchingVehicleByApplicationAutopayNumber,
    isFetchingVehicleById,
    loadCurrentVehicle,
  } = useDashboardStore();

  const {
    fetchVehicleMakesFailedMessage,
    fetchVehicleModelsFailedMessage,
    fetchVehicleTrimsFailedMessage,
    makes,
    models,
    trims,
    updateVehicleFailedMessage,
    fetchVehicleMakes,
    fetchVehicleModels,
    fetchVehicleTrims,
    updateVehicle,
  } = useVehicleConfirmationStore();

  const {
    register,
    getValues,
    setValue,
    handleSubmit,
    watch,
    formState: { errors, isValid },
  } = useForm({
    mode: 'onTouched',
    delayError: 200,
  });

  const yearValidation = {
    onChange: async (event: { target: { value: string } }): Promise<void> => {
      setValue('makeValue', '');
      setValue('modelValue', '');
      setValue('trimValue', '');

      await fetchVehicleMakes(event.target.value);
    },
    required: t('validation.year.required'),
  };

  const makeValidation = {
    onChange: async (event: { target: { value: string } }): Promise<void> => {
      setValue('modelValue', '');
      setValue('trimValue', '');

      await fetchVehicleModels(getValues('yearValue'), event.target.value);
    },
    required: t('validation.make.required'),
  };

  const modelValidation = {
    onChange: async (event: { target: { value: string } }): Promise<void> => {
      setValue('trimValue', '');

      await fetchVehicleTrims(
        getValues('yearValue'),
        getValues('makeValue'),
        event.target.value,
      );
    },
    required: t('validation.model.required'),
  };

  const trimValidation = {
    required: t('validation.trim.required'),
  };

  // Vehicle can be fetched by either application autopay number or vehicle id
  // create a loading state that combines both fetching states for simplicity
  const isFetchingVehicle =
    (isFetchingVehicleByApplicationAutopayNumber || isFetchingVehicleById) &&
    isVehicleEmpty;

  const errorMessage =
    fetchVehicleByApplicationAutopayNumberFailedMessage ||
    fetchVehicleByIdFailedMessage ||
    fetchVehicleMakesFailedMessage ||
    fetchVehicleModelsFailedMessage ||
    fetchVehicleTrimsFailedMessage ||
    updateVehicleFailedMessage ||
    null;

  const YEARS_OF_VEHICLES_TO_SHOW = 20;
  const INCLUDE_NEXT_YEAR_OFFSET = 2;

  // Present 20 years of vehicles starting from this year whilst including next year.
  // lodash range does not include its end parameter, so we add 2 to include next year
  const vehicleYears = rangeRight(
    new Date().getFullYear() - YEARS_OF_VEHICLES_TO_SHOW,
    new Date().getFullYear() + INCLUDE_NEXT_YEAR_OFFSET,
  ).map(toString);

  const navigateToDashboardGarage = (): void => {
    navigateWithCallback('/dashboard/garage').then((): void => {
      resetStore(useVehicleConfirmationStore);
    });
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const { yearValue, makeValue, modelValue, trimValue } = values;

    if (!yearValue || !makeValue || !modelValue || !trimValue) {
      return;
    }

    const make = makes.find(
      (aMake): boolean => aMake.value === makeValue,
    ).label;
    const model = models.find(
      (aModel): boolean => aModel.value === modelValue,
    ).label;
    const trim = trims.find((aTrim): boolean => aTrim.value === trimValue);

    const params: UpdateVehicleParams = {
      vehicle: {
        make,
        model,
        trim: trim.label,
        trimId: trim.value,
        year: yearValue,
      },
      // Vehicle can be fetched by either application autopay number or vehicle id
      // so we want to ensure we use the actual vehicle id (uuid) in the update vehicle request
      vehicleId,
    };

    await updateVehicle(params);

    const { updateVehicleSuccess } = useVehicleConfirmationStore.getState();

    if (updateVehicleSuccess) {
      // Reset the dashboard store to ensure a clean state when navigating to the dashboard application page
      resetStore(useDashboardStore);

      // Now that the vehicle is confirmed, the duplicate application page can be
      // displayed. It owns clearing the parameter once the applicant has seen it
      const destination =
        hasDuplicateApplication === 'true'
          ? '/duplicate-application'
          : `/dashboard/application/${vehicleId}`;

      navigateWithCallback(destination).then((): void => {
        resetStore(useVehicleConfirmationStore);
      });
    }
  };

  useEffect((): void => {
    loadCurrentVehicle(paramVehicleId);
  }, []);

  if (isFetchingVehicle) {
    return <CircularProgressFullScreen />;
  }

  return (
    <ThemeProvider theme={theme}>
      <div className="page vehicle-confirmation-page">
        <Header showDrawerToggle onBackClick={navigateToDashboardGarage} />

        <main>
          <form
            data-testid={locator.pages.vehicleConfirmation.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1 data-testid={locator.pages.vehicleConfirmation.form.h1}>
                {t('VehicleConfirmationPage.title')}
              </h1>
              <h2 data-testid={locator.pages.vehicleConfirmation.form.h2}>
                {t('VehicleConfirmationPage.subtitle')}
              </h2>

              {!isEmpty(errorMessage) && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={
                      locator.pages.vehicleConfirmation.alert.fetchOrUpdateError
                    }
                    severity="error"
                  >
                    {errorMessage}
                  </Alert>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <div>
                  <Select
                    {...register('yearValue', yearValidation)}
                    error={errors.yearValue}
                    errorTestId={
                      locator.pages.vehicleConfirmation.form.yearSelectError
                    }
                    inputTestId={
                      locator.pages.vehicleConfirmation.form.yearSelect
                    }
                    label={t('VehicleConfirmationPage.selectYear')}
                    options={vehicleYears.map((year) => ({
                      name: year,
                      value: year,
                    }))}
                  />
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <Select
                    {...register('makeValue', {
                      ...makeValidation,
                      disabled: !watch('yearValue'),
                    })}
                    error={errors.makeValue}
                    errorTestId={
                      locator.pages.vehicleConfirmation.form.makeSelectError
                    }
                    inputTestId={
                      locator.pages.vehicleConfirmation.form.makeSelect
                    }
                    label={t('VehicleConfirmationPage.selectMake')}
                    options={makes.map(({ label, value }) => ({
                      name: label,
                      value,
                    }))}
                  />
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <Select
                    {...register('modelValue', {
                      ...modelValidation,
                      disabled: !watch('yearValue') || !watch('makeValue'),
                    })}
                    error={errors.modelValue}
                    errorTestId={
                      locator.pages.vehicleConfirmation.form.modelSelectError
                    }
                    inputTestId={
                      locator.pages.vehicleConfirmation.form.modelSelect
                    }
                    label={t('VehicleConfirmationPage.selectModel')}
                    options={models.map(({ label, value }) => ({
                      name: label,
                      value,
                    }))}
                  />
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <Select
                    {...register('trimValue', {
                      ...trimValidation,
                      disabled:
                        !watch('yearValue') ||
                        !watch('makeValue') ||
                        !watch('modelValue'),
                    })}
                    error={errors.trimValue}
                    errorTestId={
                      locator.pages.vehicleConfirmation.form.trimSelectError
                    }
                    inputTestId={
                      locator.pages.vehicleConfirmation.form.trimSelect
                    }
                    label={t('VehicleConfirmationPage.selectTrim')}
                    options={trims.map(({ label, value }) => ({
                      name: label,
                      value,
                    }))}
                  />
                </div>
              </div>

              <div style={{ marginBottom: '3rem' }}>
                <Button
                  data-testid={
                    locator.pages.vehicleConfirmation.form.submitButton
                  }
                  disabled={!isValid}
                  loader={isLoading}
                  loaderPosition="end"
                  fullWidth
                  submit
                >
                  {t('VehicleConfirmationPage.confirm')}
                </Button>
              </div>
            </div>
            <footer data-testid={locator.pages.vehicleConfirmation.footer.root}>
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </div>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'VehicleConfirmationPage';
